import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Typography, Box, Grid } from '@mui/material';
import { AddCircleOutline as AddIcon } from '@mui/icons-material';

import { FBoxContainer, FHButton, FCheckbox, FHConfirmDialog, FProgressBar } from '../../../base';
import { medicationsActions, treatmentPlanActions } from '../../../../store';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import MedicationRow from './MedicationRow';
import { Themes } from '../../../../constants';
import { FORTA_WHITE } from '../../../../utils/colors';
import { TreatmentPlanPages } from '../constants/general';

import type { IMedication } from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { ChangeEvent } from 'react';

interface MedicationHeaderProps {
  editable: boolean;
}

function Medications() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const [hasMedication, setHasMedication] = useState<boolean | null | undefined>(
    treatmentPlan?.has_medication,
  );

  const {
    list: medications,
    loading,
    errorState,
    treatmentPlanId: currentTreatmentPlanId,
  } = useSelector((state: RootState) => state.medications);

  const isEditable = useTreatmentPlanEditable();

  useEffect(() => {
    if (!treatmentPlan) {
      dispatch(medicationsActions.clearState());
      return;
    }
    if (treatmentPlan?.id !== currentTreatmentPlanId) {
      dispatch(medicationsActions.clearState());
    }
    dispatch(medicationsActions.updateState({ key: 'treatmentPlanId', data: treatmentPlan?.id }));

    setHasMedication(treatmentPlan?.has_medication);

    dispatch(medicationsActions.getList({ getAccessTokenSilently, planId: treatmentPlan?.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan]);

  useEffect(() => {
    if (!treatmentPlan?.id) return;

    if (
      hasMedication === undefined ||
      hasMedication === null ||
      (hasMedication && medications.length === 0)
    ) {
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.Medications]: '!',
        }),
      );
    } else {
      let count = 0;
      for (const key in errorState) {
        count += errorState[key];
      }
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.Medications]: count,
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorState, hasMedication, medications]);

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const handleCreateMedication = () => {
    if (hasMedication === null || hasMedication === undefined) handleChangeHasMedication(true);
    dispatch(medicationsActions.addUnsavedMedication());
  };

  const handleChangeHasMedication = (hasMedication: boolean) => {
    if (!treatmentPlan?.id) return;

    if (medications.length > 0 && hasMedication === false) return setShowConfirmation(true);

    dispatch(
      treatmentPlanActions.updateTreatmentPlan({
        treatmentPlanId: treatmentPlan?.id,
        getAccessTokenSilently,
        data: {
          client_id: treatmentPlan?.client_id,
          has_medication: hasMedication,
        },
      }),
    );
  };

  const handleServiceNeedChange = () => {
    setShowConfirmation(false);
    if (!treatmentPlan?.id) return;
    dispatch(
      treatmentPlanActions.updateTreatmentPlan({
        treatmentPlanId: treatmentPlan?.id,
        getAccessTokenSilently,
        data: {
          client_id: treatmentPlan?.client_id,
          has_medication: false,
        },
      }),
    );
    dispatch(medicationsActions.updateState({ key: 'list', data: [] }));
  };

  const MedicationTableHeader = ({ editable }: MedicationHeaderProps) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {editable && <Box width={35}></Box>}
      <Grid container spacing={2} mb={1}>
        <Grid item xs={3}>
          <Typography fontWeight="bold">Name of medication*</Typography>
        </Grid>
        <Grid item xs={1}>
          <Typography fontWeight="bold">Dosage*</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography fontWeight="bold">Start date*</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography fontWeight="bold">Prescribing physician*</Typography>
        </Grid>
        <Grid item xs={3.5}>
          <Typography fontWeight="bold">Reason for taking*</Typography>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <>
      {loading && <FProgressBar />}
      <FBoxContainer colorTheme={Themes.Primary} title="Medications" sx={{ mt: 3 }}>
        <Box
          sx={{
            display: 'flex',
            mb: medications.length === 0 ? 0 : 1,
            alignItems: 'center',
          }}
        >
          <FCheckbox
            data-testid="noMedicationTakenByClientCheckBox"
            colorTheme={Themes.Primary}
            checked={hasMedication === false}
            disabled={!isEditable}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChangeHasMedication(!e.target.checked)
            }
            sx={{ mr: 0, fontWeight: 'bold' }}
          />
          <Typography>No medication taken by the client</Typography>
        </Box>
        {medications.length > 0 && <MedicationTableHeader editable={isEditable} />}
        {treatmentPlan &&
          medications.map((medication: IMedication, index: number) => (
            <MedicationRow
              editable={isEditable}
              medication={medication}
              planId={treatmentPlan?.id}
              key={index}
              index={index}
            />
          ))}
        {isEditable && (
          <Box
            sx={{
              display: 'flex',
              mt: medications.length === 0 ? 0 : 1,
              justifyContent: 'end',
            }}
          >
            <FHButton
              data-testid="addMedicationButton"
              disabled={!treatmentPlan?.id || hasMedication === false}
              colorTheme={Themes.Primary}
              onClick={handleCreateMedication}
              startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
              error={hasMedication !== false && medications?.length === 0}
            >
              Add Medication
            </FHButton>
          </Box>
        )}
        <FHConfirmDialog
          open={showConfirmation}
          colorTheme={Themes.Primary}
          content={'This action will delete medications, are you sure you want to continue?'}
          submittext={'CONTINUE'}
          canceltext={'BACK'}
          onClose={() => setShowConfirmation(false)}
          onSubmit={handleServiceNeedChange}
        />
      </FBoxContainer>
    </>
  );
}

export default Medications;
